import React, { useEffect, useState } from "react";
import { useTheme } from "../../context/ThemeContext";
import {
  getPrimaryText,
  getSecondaryText,
  getLightBorder,
} from "../../utils/themeUtils";
import { LMSEnrollment } from "./types";
import { getEnrollments } from "../../api/lmsAPI";
import { Link, useLocation } from "react-router-dom";
import { Card, SectionHeading } from "../ui";

interface MemberCoursesWidgetProps {
  athleteIds: string[];
}

const MemberCoursesWidget: React.FC<MemberCoursesWidgetProps> = ({ athleteIds }) => {
  const { isDarkMode } = useTheme();
  const location = useLocation();
  const [enrollments, setEnrollments] = useState<LMSEnrollment[]>([]);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem("authToken");
  const pathParts = location.pathname.split("/").filter(Boolean);
  const tenantId = pathParts[0] || "";

  useEffect(() => {
    const fetchEnrollments = async () => {
      if (!token || athleteIds.length === 0) {
        setLoading(false);
        return;
      }
      try {
        // Fetch enrollments for all athletes
        const allEnrollments: LMSEnrollment[] = [];
        for (const athleteId of athleteIds) {
          const result = await getEnrollments(token, { athlete_id: athleteId, status: "active" });
          if (result.success && result.data) {
            allEnrollments.push(...result.data);
          }
        }
        setEnrollments(allEnrollments);
      } catch {
        // silent fail
      } finally {
        setLoading(false);
      }
    };
    fetchEnrollments();
  }, [token, athleteIds]);

  if (loading) return null;
  if (enrollments.length === 0) return null;

  return (
    <Card padding="md">
      <SectionHeading
        actions={
          <Link
            to={`/${tenantId}/manage/lms`}
            className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
          >
            View All
          </Link>
        }
      >
        Active Courses
      </SectionHeading>

      <div className="space-y-3">
        {enrollments.slice(0, 5).map((enrollment) => (
          <div key={enrollment.id} className={`p-3 rounded border ${getLightBorder(isDarkMode)}`}>
            <div className="flex items-center justify-between mb-1">
              <span className={`text-sm font-medium ${getPrimaryText(isDarkMode)}`}>
                {enrollment.gp_course_id ? "GP Course" : "Local Course"}
              </span>
              <span className={`text-xs px-2 py-0.5 rounded ${
                enrollment.course_source === "gp"
                  ? "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200"
                  : "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
              }`}>
                {enrollment.course_source.toUpperCase()}
              </span>
            </div>
            {enrollment.athletes && (
              <p className={`text-xs ${getSecondaryText(isDarkMode)} mb-2`}>
                {enrollment.athletes.first_name} {enrollment.athletes.last_name}
              </p>
            )}
            {/* Progress bar */}
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all"
                style={{ width: `${enrollment.progress}%` }}
              />
            </div>
            <p className={`text-xs ${getSecondaryText(isDarkMode)} mt-1`}>
              {enrollment.progress}% complete
            </p>
          </div>
        ))}
      </div>
    </Card>
  );
};

export default MemberCoursesWidget;
